import React, { useEffect, useState } from "react";
import { ActivityCard, MixedText, PlayButton, ProgressPill } from "../shared";

/**
 * NoStamp — "The لَا Stamp" for the negation lesson.
 *
 * Each row holds an affirmative sentence and a لَا stamp button. Tapping
 * the stamp slides لَا in front of the verb — highlighted in rose — and
 * plays the negated sentence; tapping again restores the affirmative so
 * the two can be compared. Completes when every row has been stamped once.
 */
const LA = "لَا";

function StampedSentence({ text }) {
  const idx = text.indexOf(LA);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: "var(--tag-rose)" }}>{LA}</span>
      {text.slice(idx + LA.length)}
    </>
  );
}

export default function NoStamp({ data, onComplete, audio }) {
  const [stamped, setStamped] = useState(() => new Set());
  const [everStamped, setEverStamped] = useState(() => new Set());

  const done = everStamped.size >= data.items.length;

  useEffect(() => {
    if (done) onComplete();
  }, [done, onComplete]);

  const handleStamp = (item) => {
    const isStamped = stamped.has(item.id);
    setStamped((prev) => {
      const next = new Set(prev);
      if (isStamped) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
    if (!isStamped) {
      setEverStamped((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
    }
    if (audio) audio.play(isStamped ? item.yes_ar : item.no_ar);
  };

  return (
    <ActivityCard>
      <div className="flex justify-end mb-3">
        <ProgressPill>{everStamped.size}/{data.items.length} stamped</ProgressPill>
      </div>

      <div className="flex flex-col gap-2.5">
        {data.items.map((item) => {
          const isStamped = stamped.has(item.id);
          const seen = everStamped.has(item.id);
          return (
            <div
              key={item.id}
              className="flex items-center gap-3 px-4 py-3 rounded-xl"
              style={{
                background: isStamped ? "var(--tag-rose-bg)" : "var(--ink)",
                border: seen ? "1px solid var(--tag-rose)" : "1px solid var(--panel-line)",
              }}
            >
              <button
                type="button"
                onClick={() => handleStamp(item)}
                aria-pressed={isStamped}
                className="shrink-0 rounded-lg px-3 py-2 transition-all hover:brightness-110"
                style={{
                  background: isStamped ? "var(--tag-rose)" : "var(--ink)",
                  border: "1px solid var(--tag-rose)",
                  color: isStamped ? "var(--parchment)" : "var(--tag-rose)",
                }}
              >
                <span lang="ar" dir="rtl" className="font-arabic text-2xl leading-[1.4] block">
                  {data.stamp_label}
                </span>
              </button>

              <div className="flex-1 min-w-0">
                <p dir="rtl" lang="ar" className="font-arabic text-2xl leading-[1.7] m-0" style={{ color: "var(--parchment)" }}>
                  {isStamped ? <StampedSentence text={item.no_ar} /> : item.yes_ar}
                </p>
                <p className="text-sm mt-0.5 mb-0" style={{ color: "var(--parchment-dim)" }}>
                  {isStamped ? item.no_en : item.yes_en}
                </p>
                {isStamped && item.note && (
                  <p className="text-sm mt-1.5 mb-0 px-3 py-1.5 rounded-lg inline-block" style={{ background: "var(--gold-bg)", color: "var(--parchment)" }}>
                    <MixedText text={item.note} />
                  </p>
                )}
              </div>

              {audio && (
                <PlayButton
                  text={isStamped ? item.no_ar : item.yes_ar}
                  playingText={audio.playingText}
                  onPlay={audio.play}
                />
              )}
            </div>
          );
        })}
      </div>

      {done && (
        <div
          className="flex items-start gap-2.5 mt-4 px-4 py-3 rounded-xl"
          style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}
        >
          <span className="text-lg leading-none" aria-hidden="true">🚫</span>
          <p className="text-sm leading-relaxed m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.takeaway} />
          </p>
        </div>
      )}
    </ActivityCard>
  );
}
